import React, { useEffect, useState } from 'react';
import {Link, NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPenToSquare,
  faEllipsis,
  faSitemap,
  faList,
  faPlay,
  faFolderPlus,
  faFileArrowUp,
  faPaperPlane,
  faPaste,
  faUserCheck
} from '@fortawesome/free-solid-svg-icons';
import Dropdown from 'react-bootstrap/Dropdown';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import ActivityTable from './components/ActivityTable';
import { useExportActivitiesMutation, useGetActivitiesByFilterMutation } from '../../features/authentication/activityApiSlice';
import { useUpdateActivityFiltersMutation } from '../../features/authentication/persistApiSlice';
import { useActivityContext } from './context';
import ActivityTypes from '../../enums/activityTypes';
import MoveMenuModal from '../assignments/components/assignmentFloatingMenu/modals/MoveMenuModal';
import DuplicateMenuModal from '../assignments/components/assignmentFloatingMenu/modals/DuplicateMenuModal';
import ActivitiesShareMenuModal from './components/ActivitiesFloatingMenuModals/ActivitiesShareMenuModal';
import { useDashboardContext } from '../dashboard/context';
import DeleteMenuModal from '../assignments/components/assignmentFloatingMenu/modals/DeleteMenuModal';
import { useAssignmentContext } from '../assignments/context';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

const ActivitiesList = () => {
  const [loadMoreData, setLoadMoreData] = useState([]);
  const [loadMegaDropDown, setLoadMegaDropDown] = useState([]);
  const context = useActivityContext();
  const assignmentContext = useAssignmentContext();
  const [getActivitiesByFilter] = useGetActivitiesByFilterMutation();
  const [updateActivityFilters] = useUpdateActivityFiltersMutation();
  const [sortValue, setSortValue] = useState(null);
  const [duplicateActivityModal, setDuplicateActivityModal] = useState(false);
  const [skipValue, setSkipValue] = useState(0);
  const [isCurrentPageSelected, setIsCurrentPageSelected] = useState(false);
  const [moveActivityModal, setMoveActivityModal] = useState(false);
  const [exportActivities] = useExportActivitiesMutation();
  const [shareActivityModal, setShareActivityModal] = useState(false);
  const [deleteActivityModal, setDeleteActivityModal] = useState(false);
  const dashboardContext = useDashboardContext();

  const [isLoading, setIsLoading] = useState(false);

  const getAllActivitiesByFilterAsync = async (skip, take) => {
    try {
      const options = {
        sort: [context.sortColumn, context.sortDirection == 'DESC'? 1 : 0],
        ActivityStatus: context.filters.activityStatus,
        SearchText: context.filters.selectedKeyWord,
        CourseId: context.filters.courseId,
        FolderId: context.filters.folderId,
        IsSearch: Boolean(context.search)
      };

      const params = new URLSearchParams();
      const { ActivityStatus, CourseId, FolderId} = options;
      params.append('CourseId', CourseId);
      params.append('FolderId', FolderId);
      params.append('sortDirection', context.sortDirection);
      if (options.SearchText) params.append('SearchText', options.SearchText);

      if (ActivityStatus?.length) {
        ActivityStatus.forEach(id => {
          params.append('ActivityStatus', id);
        });
      }

      let updatedSort = [...options.sort];

      if (updatedSort[0] === 'ActivityName') {
        updatedSort[0] = 'AssignmentName';
      } else if (updatedSort[0] === 'VocabularyCount') {
        updatedSort[0] = 'Questions';
      }

      params.append('sort', updatedSort);
      if(options.IsSearch){
        const filterOptions = {
          activityStatus: ActivityStatus?.length ? ActivityStatus : [],
          courseId: CourseId,
          folderId: FolderId,
          sortColumn: context.sortColumn,
          sortDirection: context.sortDirection,
          takeSize: take
        };
        await updateActivityFilters(filterOptions).unwrap();
      }

      const newOptions = {
        skip: skip,
        take: take,
        params: params
      };
      return await getActivitiesByFilter(newOptions).unwrap();
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (context.filtersInitialized && context.isActivitiesAvailable) {
      if (context.search) {
        context.setAssignedData([]);
      }
      if (sortValue || context.search) {
        setLoadMoreData([]);
        setSkipValue(0);
      }

      loadInitialData();
    }
  }, [context.search, sortValue, context.assignedDataStatus]);

  const loadInitialData = async () => {
    setIsLoading(true);
    const { total, data } = await getAllActivitiesByFilterAsync(0, context.filters.takeSize);
    distinctObjects(data);
    context.setNoOfActivities(total);
    setIsLoading(false);
    setIsCurrentPageSelected(false);
    context.setIsAllSelected(false);
  };

  const nextCallback = async (newOffset) => {
    setIsLoading(true);
    setSkipValue(newOffset);
    const { total, data } = await getAllActivitiesByFilterAsync(newOffset, context.filters.takeSize);
    let newItems = [...loadMoreData, ...data];
    if(isCurrentPageSelected){
      newItems = newItems.map((item) => {
        return { ...item, isChecked: true };
      });
    }
    distinctObjects(newItems);
    context.setNoOfActivities(total);
    setIsLoading(false);
  };

  const distinctObjects = (data) => {
    const distinctData = Array.from(
      data.reduce((map, obj) => {
        map.set(obj.assignmentId, {
          ...obj,
          folderName: obj.folderName?.replace(
            /shared assignments?/i,
            'Shared Flashcards'
          )
        });
        return map;
      }, new Map()).values()
    );
    setLoadMoreData(distinctData);
  };
  
  const megaListDropdown = () => {
    const megaDropdown = [
      {
        id: 'flashcardChanges',
        name: 'View Flashcard Changes',
        icon: <FontAwesomeIcon icon={faPaste} />,
        link: 'manage_assign/reports/assignment_changes_report'
      },
      {
        id: 'printFlashcard',
        name: 'Print Flashcard',
        icon: <FontAwesomeIcon icon={faPlay} />,
        link: 'vocabularyPrinting'
      },
      {
        id: 'delete',
        name: 'Delete Flashcard',
        icon: <FontAwesomeIcon icon={faSitemap} />,
        modal: () => setDeleteActivityModal(!deleteActivityModal)
      },
      {
        id: 'sendcopy',
        name: 'Send Copy',
        icon: <FontAwesomeIcon icon={faFolderPlus} />,
        modal: () => setShareActivityModal(!shareActivityModal)
      },
      {
        id: 'move',
        name: 'Move to Folder',
        icon: <FontAwesomeIcon icon={faFileArrowUp} />,
        modal: () => setMoveActivityModal(!moveActivityModal)
      },
      {
        id: 'export',
        name: 'Export',
        icon: <FontAwesomeIcon icon={faList} />,
      },
      {
        id: 'duplicate',
        name: 'Duplicate',
        icon: <FontAwesomeIcon icon={faPaperPlane} />,
        modal: () => setDuplicateActivityModal(!duplicateActivityModal)
      }
    ];
    setLoadMegaDropDown(megaDropdown);
  };

  const handleChange = (e) => {
    const { name, checked } = e.target;
    if (name === 'allselect') {
      let tempLoadMoreData = loadMoreData.map((item) => {
        return { ...item, isChecked: checked };
      });
      setLoadMoreData(tempLoadMoreData);
      context.setAssignedData(tempLoadMoreData.filter((item) => item.isChecked));
      setIsCurrentPageSelected(!isCurrentPageSelected);
      const selectedCount = tempLoadMoreData.filter(item=> item.isChecked)?.length;
      context.setIsAllSelected((context.noOfActivities == selectedCount && context.noOfActivities <= context.filters.takeSize));
      context.setSelectedCount(selectedCount);
    } else {
      const id = parseInt(name);
      let loadMoreDataArray = loadMoreData.map((item) =>
        item.assignmentId === id ? { ...item, isChecked: checked } : item
      );
      const selectedCount = loadMoreDataArray.filter(item => item.isChecked).length;
      setIsCurrentPageSelected(selectedCount == loadMoreData.length);
      context.setIsAllSelected((context.noOfActivities == selectedCount && context.noOfActivities <= context.filters.takeSize));
      context.setSelectedCount(selectedCount);
      const selectedData = loadMoreData.find((item) => item.assignmentId === id);

      let assignedDataArray = [];
      if (
        context.assignedData.filter((item) => item.assignmentId === selectedData.assignmentId).length > 0
      ) {
        context.setAssignedData(
          context.assignedData.filter((item) => item.assignmentId !== selectedData.assignmentId)
        );
      } else {
        assignedDataArray.push(...context.assignedData, selectedData);
        context.setAssignedData(assignedDataArray);
      }
      setLoadMoreData(loadMoreDataArray);
    }
  };

  useEffect(() => {
    megaListDropdown();
  }, []);

  const exportActivitiesAsync = async (activityId) => {
    try {
      const assignmentStatus = context.selectedStatusId?.length ? context.selectedStatusId : [];
      const hasSearchText = context.selectedKeyWord.length > 0;

      const option = {
        courseId: context.selectedCourseId,
        folderId: Number(context.selectedFolderId ?? 0),
        sortColumn: context.sortColumn,
        sortDirection: context.sortDirection,
        assignmentStatus: assignmentStatus.map(Number),
        assignedVia: [],
        isCollapsed: Boolean(context.filters?.isCollapsed),
        isSearch: hasSearchText,
        searchText: hasSearchText ? context.selectedKeyWord : null,
        assignmentIds: [activityId]
      };

      const blob = await exportActivities(option).unwrap();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = 'CastleXMLFlashCards.xml';
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
    }
  };

  const generateMoreOptions = (item,index)=> {
    return(
      <div className="action-btns d-flex justify-content-end gap-2">
        <NavLink 
          onClick={() => {
            assignmentContext.setAssignmentId(item.assignmentId);
            assignmentContext.setAssignmentName(item.assignmentName);
            assignmentContext.setAssignedCourseName([item.courseName]);
            assignmentContext.setAssignmentAssignedViaDetails({ id: item.assignedVia });
            assignmentContext.setAssignedStatus(item.assignedStatus);
            assignmentContext.setNoOfAssignedStudents(item.assignedCount);
          }}
          to={`/assignments/${item.assignmentId}/${ActivityTypes.FlashCard}/manage_assign`}>
          <OverlayTrigger
            key={`assignmentMode-${index}`}
            placement="top"
            overlay={<Tooltip className="custom-tooltip"> {item.noOfQuestion == 0 ? 'Requires vocabulary to assign/manage students' : 'Manage/Assign'} </Tooltip>}
          >
            <button className={item.noOfQuestion == 0 ? 'text-grey' : ''} id={`manage${item.assignmentId}`} disabled ={item.noOfQuestion == 0}>
              <FontAwesomeIcon className='fs-14' icon={faUserCheck} />
            </button>
          </OverlayTrigger>
         
        </NavLink>
        <NavLink 
          end
          to={`${item.assignmentId}/edit_activity`}
          onClick={() => {
            context.setActivityId(item.assignmentId);
            context.setActivityName(item.assignmentName);
            context.setActivityCourseId(item.courseId);
          }}>
          <OverlayTrigger
            key={`assignmentMode-${index}`}
            placement="top"
            overlay={<Tooltip className="custom-tooltip">View/Edit</Tooltip>}
          >
            <button id={`edit-${item.assignmentId}`}>
              <FontAwesomeIcon className='fs-14' icon={faPenToSquare} />
            </button>
          </OverlayTrigger>
        </NavLink>
        <div className="position-relative">
          <ReactTooltip
            id={`moreactions-${index}`}
            place="top"
            content="More Actions"
            className="list-tooltip z-index10"
          />
          <Dropdown
            data-tooltip-id={`moreactions-${index}`}
          >
            <Dropdown.Toggle>
              <FontAwesomeIcon className='fs-14' icon={faEllipsis} />
            </Dropdown.Toggle>
            <Dropdown.Menu className="more-dropdown assignment-more p-3 pe-0">
              {loadMegaDropDown.map((val, arrayKey) => {
                const isLinkItem = val.id === 'flashcardChanges' || val.id === 'printFlashcard';

                const handleClick = () => {
                  if (val.id === 'export') {
                    exportActivitiesAsync(item.assignmentId);
                    return;
                  }
                  if (val.id === 'sendcopy') 
                    dashboardContext.setSelectRecipient(true);

                  val.modal?.();
                  context.setActivityId(item.assignmentId);
                };

                if (isLinkItem) {
                  return (
                    <Dropdown.Item
                      key={`drop-${arrayKey}`}
                      as={Link}
                      to={`/assignments/${item.assignmentId}/${val.link}/${ActivityTypes.FlashCard}`}
                      className='d-flex align-items-center'
                      state={{
                        assignmentName: item.assignmentName,
                        assignmentId: item.assignmentId
                      }}>
                      {val.icon}
                      {val.name}
                    </Dropdown.Item>
                  );
                }

                return (
                  <Dropdown.Item
                    key={`drop-${arrayKey}`}
                    className='d-flex align-items-center'
                    onClick={handleClick}>
                    {val.icon}
                    {val.name}
                  </Dropdown.Item>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    );
  };
  
  return (
    <>
      {(context.isAllSelected && isCurrentPageSelected)?
        <div>All selected</div>
        :
        isCurrentPageSelected ? 
          <div className='mb-2'>All <strong>{loadMoreData.length}</strong> flashcards in this page are selected. <span className='text-info cursor-pointer' onClick={()=> context.setIsAllSelected(true)}>Select all flashcards that match this search</span></div>
          : ''
      }
      <div className="assignment-table table-responsive mt-2">
        <ActivityTable
          data={loadMoreData}
          nextCallback={nextCallback}
          skipValue={skipValue}
          limit={context.filters.takeSize}
          totalCount={context.noOfActivities}
          setSortValue={setSortValue}
          moreOption={generateMoreOptions}
          handleSelect={handleChange}
          isLoading={isLoading}
          isCurrentPageSelected={isCurrentPageSelected}
        />
      </div>
      {moveActivityModal && (
        <MoveMenuModal
          modalData={moveActivityModal}
          setModalData={setMoveActivityModal}
          fromMoreActions={true}
          activityType={ActivityTypes.FlashCard}
        />
      )}
      {duplicateActivityModal && (
        <DuplicateMenuModal
          modalData={duplicateActivityModal}
          setModalData={setDuplicateActivityModal}
          fromMoreActions={true}
          activityType={ActivityTypes.FlashCard}
        />
      )}
      {shareActivityModal &&
        <ActivitiesShareMenuModal
          assignmentId={[context.activityId]}
          fromMoreAction={true}
          isModalOpen={setShareActivityModal}
        />
      }
      {deleteActivityModal && (
        <DeleteMenuModal
          modalData={deleteActivityModal}
          setModalData={setDeleteActivityModal}
          fromMoreActions={true}
          activityType={ActivityTypes.FlashCard}
        />
      )}
    </>
  );
};

export default ActivitiesList;